import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { ApiError, AuthService, Login } from '../../api';
import { FormFeedback } from '../../app/components/FormFeedback';
import { FormTextField } from '../../app/components/FormTextField';
import { Logo } from '../../app/components/Logo';
import { PasswordField } from '../../app/components/PasswordField';
import { SessionContext } from '../../app/contexts/SessionContext';
import { userQueryKeys } from '../../app/query-keys-factories/user';
import { setFormErrorsFromApiErrors } from '../../utils/forms';

const validationSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email({
    message: "That email doesn't look quite right.",
  }),
  password: z.string().min(1, { message: 'Password is required' }),
});

interface LoginFormProps {
  showSessionExpiredMessage?: boolean;
}

export const LoginForm = ({ showSessionExpiredMessage = false }: LoginFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const nextUrl = params.get('next');
  const { setSessionExists } = useContext(SessionContext);
  const {
    handleSubmit,
    formState: { errors },
    register,
    clearErrors,
    setError,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const queryClient = useQueryClient();
  const mutationLogin = useMutation({
    mutationFn: async (data: Login) => {
      localStorage.removeItem('session');
      await AuthService.authLoginCreate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all() });
      if (nextUrl) {
        navigate(decodeURIComponent(nextUrl));
      } else {
        navigate('/app');
      }
    },
  });

  const onSubmit = async (data) => {
    try {
      await mutationLogin.mutateAsync(data);
      setSessionExists();
    } catch (errors) {
      if (errors?.body?.non_field_errors?.includes('E-mail is not verified.')) {
        localStorage.setItem('authLastSigninEmail', data.email);
        return navigate('/register/email-confirmation');
      }
      setFormErrorsFromApiErrors(errors, setError);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="logo-container">
        <Logo className="logo" variant="color" />
      </div>
      {((mutationLogin.error as ApiError)?.body?.non_field_errors || showSessionExpiredMessage) && (
        <div className="tw:bg-stoplight-danger/20 tw:rounded-lg tw:p-2 tw:text-center">
          <FormFeedback serverErrors={(mutationLogin.error as any)?.body?.non_field_errors} />
          {showSessionExpiredMessage && (
            <div className="tw:text-sm tw:text-stoplight-danger">
              Your session has expired. Please log in again.
            </div>
          )}
        </div>
      )}
      <div className="mb-2">
        <FormTextField
          name="email"
          label=""
          type="email"
          placeholder="name@email.com"
          className="my-3 py-0"
          register={register}
          errors={errors}
        />
        <PasswordField
          name="password"
          label=""
          placeholder="Password"
          className="my-3 py-0"
          register={register}
          errors={errors}
        />
      </div>
      <div>
        <Button
          size="lg"
          className="btn-shadow w-100 mt-3"
          type="submit"
          onSubmit={() => clearErrors()}
        >
          Log in
        </Button>
      </div>
      <div className="w-100 text-center mt-4">
        <Link to="/forgot-password">Reset Password</Link>
      </div>
    </Form>
  );
};
